import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {Component, Plugins, EvalEnv, Variable} from '@packages/systems/wfdl';
import {Expression} from '@packages/systems/wfdl-ast';
import {qualify, identifier} from '@packages/systems/wfdl-Qualified';
import {
  dataTypesFromComponents,
  filterDivergentRichTextNodes,
  reifyToExpressions,
} from '@packages/systems/core/render';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {getDynamoRichTextToRichTextChildrenImpl} from '@packages/systems/plugins-utils/DynamoGateway';
import {reportBugOnce} from '@packages/utilities/reporter';
import {EvaluatedData} from './types';

const dynamoRichTextToRichTextChildrenVar = Variable.qualified(
  qualify(
    Plugins.name('DynamoGateway'),
    identifier('dynamoRichTextToRichTextChildren')
  )
);

const insertDynamoRichTextOverride = weakMemo((env: EvalEnv.EvalEnvType) =>
  weakMemo((impl: (dynamoRichText: unknown) => EvaluatedData) =>
    EvalEnv.insertValue(env, dynamoRichTextToRichTextChildrenVar, impl)
  )
);

type DynamoRichTextDeps = {
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  evalExpr: (expr: Expression) => EvaluatedData;
};

/**
 * Builds the `dynamoRichTextToRichTextChildren` impl for a given deps ref.
 * The `deps` object is a stable per-instance `useRef` container whose
 * `components`, `plugins`, and `evalExpr` fields are mutated on each render.
 * Because the ref object itself is identity-stable, `weakMemo` returns the
 * same `impl` for the lifetime of the component instance without needing a
 * `useMemo` hook.
 */
const buildDynamoRichTextImpl = weakMemo((deps: DynamoRichTextDeps) =>
  getDynamoRichTextToRichTextChildrenImpl((jsonNodes: JSONNode[]) =>
    evalJsonNodes(jsonNodes, deps.components, deps.plugins, deps.evalExpr)
  )
);

/**
 * Converts JSON nodes to evaluated elements
 */
export function evalJsonNodes(
  jsonNodes: JSONNode[],
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  evalExpr: (expr: Expression) => EvaluatedData
): EvaluatedData {
  try {
    const dataTypes = dataTypesFromComponents(plugins, components);
    const {jsonContentNodes} = filterDivergentRichTextNodes(
      plugins,
      dataTypes,
      jsonNodes
    );
    return reifyToExpressions(jsonContentNodes, dataTypes, plugins).map(
      evalExpr
    );
  } catch {
    reportBugOnce(
      'DynamoRichTextReificationFailed',
      'Failed to reify rich text JSON nodes; returning empty array fallback',
      {
        team: 'cms-authoring',
        ticket: 'https://webflow.atlassian.net/browse/CMSAUTH-4980',
        nodeCount: jsonNodes.length,
      },
      {skipConsole: true}
    );
    return [];
  }
}

/**
 * Replaces `DynamoGateway::dynamoRichTextToRichTextChildren` in the EvalEnv
 * with an implementation that has access to the renderer's `components`,
 * `plugins` and `evalExpr`.
 *
 * At bootstrap the plugin registers a noop default (returns []) because
 * `components`, `plugins` and `evalExpr` are not yet available. This hook
 * overrides that default at mount time by composing
 * `getDynamoRichTextToRichTextChildrenImpl` with `evalJsonNodes` as the
 * JSONNode evaluator.
 *
 * Returns EvaluatedData so listOfElementsToText and the renderer can handle
 * the output without adding a new evaluated element comprehension to WFDL.
 *
 * When? The replacement is created once per component instance via
 * `weakMemo` keyed on a stable ref object. On each call it reads current
 * `components`, `plugins` and `evalExpr` from the ref, classifies the raw
 * value (None, legacy HTML string, or JSONNode array), and evaluates
 * JSONNode arrays.
 *
 * Why useRef? The replacement's identity must stay stable because
 * topEvalEnv is compared by === in propsEqualUtils.ts. A new function
 * reference would force every element to re-render. useRef is preferred
 * over a createRender closure so the react-compiler can still optimize
 * the component. `weakMemo` on the ref object avoids the memory overhead
 * of a `useMemo` hook (deps array, hook slot, closure).
 */
export const useDynamoRichTextToRichTextChildren = (
  baseTopEvalEnv: EvalEnv.EvalEnvType,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  evalExpr: (expr: Expression) => EvaluatedData
): EvalEnv.EvalEnvType => {
  const depsRef = React.useRef<DynamoRichTextDeps>({
    components,
    plugins,
    evalExpr,
  });
  depsRef.current.components = components;
  depsRef.current.plugins = plugins;
  depsRef.current.evalExpr = evalExpr;

  const impl = buildDynamoRichTextImpl(depsRef.current);

  return insertDynamoRichTextOverride(baseTopEvalEnv)(impl);
};
